import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useFacilityBilling } from '../../hooks/useFacilityBilling';
import { summarizeBilling, BILLING_EMAIL } from '../../utils/billingStatus';
import '../../styles/BillingBanner.css';

// Dismissal is per session and per status, not per day: dismissing the 3-day trial
// notice also hides the 1-day one until the next sign-in. Decided in A4 review.
const dismissKey = (facilityId, status) => `billingBannerDismissed:${facilityId}:${status}`;

function copyFor(summary) {
    if (summary.status == null) {
        // Facility doc has no billing map. The backend gate returns 402 status=missing,
        // so say why instead of leaving the user with only the modal.
        return { tone: 'blocked', title: 'No active plan.', body: 'New cases are paused until billing is set up.', dismissible: false };
    }
    if (summary.status === 'trialing') {
        const n = summary.trialDaysLeft ?? 0;
        return { tone: 'trial', title: `Your trial ends in ${n} day${n === 1 ? '' : 's'}.`, body: 'Contact us to activate your agency plan.', dismissible: true };
    }
    if (summary.status === 'past_due') {
        return { tone: 'blocked', title: summary.trialExpired ? 'Trial ended.' : 'Payment needed.', body: 'New cases are paused until billing is resolved.', dismissible: false };
    }
    if (summary.status === 'canceled') {
        return { tone: 'blocked', title: 'Subscription canceled.', body: 'New cases are paused. Contact us to reactivate.', dismissible: true };
    }
    return null;
}

export default function BillingBanner() {
    const { user, facilityId, isFacilityAdmin, profileReady } = useAuth();
    const { billing, loading, error } = useFacilityBilling(user ? facilityId : null);
    const [dismissedKey, setDismissedKey] = useState(null);

    // No facility: nothing to say (site admins and internal accounts land here).
    // Read error: stay silent rather than guess; the 402 modal still explains a block.
    if (!user || !profileReady || !facilityId || loading || error) return null;
    const summary = summarizeBilling(billing);
    const copy = copyFor(summary);
    if (!copy) return null;

    const key = dismissKey(facilityId, summary.status);
    if (copy.dismissible && (dismissedKey === key || sessionStorage.getItem(key) === '1')) return null;

    const dismiss = () => { sessionStorage.setItem(key, '1'); setDismissedKey(key); };

    return (
        <div className={`billing-banner billing-banner--${copy.tone}`} role="status">
            <div className="billing-banner-text">
                <strong>{copy.title}</strong> {copy.body}{' '}
                <a href={`mailto:${BILLING_EMAIL}`} className="billing-banner-link">{BILLING_EMAIL}</a>
            </div>
            <div className="billing-banner-actions">
                {isFacilityAdmin && <Link to="/billing" className="billing-banner-cta">Go to Billing</Link>}
                {copy.dismissible && (
                    <button type="button" className="billing-banner-dismiss" onClick={dismiss} aria-label="Dismiss">×</button>
                )}
            </div>
        </div>
    );
}
